/**
 * Sentry browser monitoring.
 * Every event carries device/browser (parsed by Sentry from the user agent), screen and network
 * details, the current page and dashboard section, the user id/role, and a timestamp.
 * API calls are watched for 5xx responses, network failures and slow responses.
 *
 * Configured at build time:
 *   REACT_APP_SENTRY_DSN                 (required to enable; monitoring is a no-op without it)
 *   REACT_APP_SENTRY_ENVIRONMENT         (default: NODE_ENV)
 *   REACT_APP_SENTRY_RELEASE             (set by the deploy scripts to syllera@<12-char commit>,
 *                                         the same release the backend reports)
 *   REACT_APP_SENTRY_TRACES_SAMPLE_RATE  (default: 0.1 in production, 1.0 otherwise)
 *   REACT_APP_SENTRY_SLOW_API_MS         (default: 5000)
 */
import * as Sentry from '@sentry/react';

const RAW_DSN = (process.env.REACT_APP_SENTRY_DSN || '').trim();
// Placeholders such as "your_dsn_here" must not enable monitoring.
const DSN = /^https?:\/\/[^@\s/]+@[^/\s]+\/\d+$/.test(RAW_DSN) ? RAW_DSN : '';
const IS_PROD = process.env.NODE_ENV === 'production';
const SLOW_API_MS = Number(process.env.REACT_APP_SENTRY_SLOW_API_MS) || 5000;
const USER_DASHBOARD_SECTION_KEY = 'syllera-user-dashboard-section';

export let isMonitoringEnabled = Boolean(DSN);

const reportedErrors = new WeakSet();

/** Exclude an error from reporting: it is expected (auth, validation) or was already reported. */
export function markHandled(error) {
  if (error && typeof error === 'object') reportedErrors.add(error);
  return error;
}

export function wasReported(error) {
  return Boolean(error && typeof error === 'object' && reportedErrors.has(error));
}

/** Report a caught error once (later calls with the same Error object are ignored). */
export function reportError(error, captureContext) {
  if (!isMonitoringEnabled || wasReported(error)) return;
  markHandled(error);
  Sentry.captureException(error, captureContext);
}

function pathOnly(url) {
  try {
    return url ? new URL(url, window.location.href).pathname : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Report a student-facing load or save failure that left the UI in an error or empty state.
 * 5xx and network errors are already reported by the fetch monitor, and 401 (session) / 429
 * (rate limit) are expected, so only unexpected statuses and exceptions are reported here.
 */
export function reportLoaderFailure({ feature, response, error, endpoint }) {
  if (!isMonitoringEnabled) return;
  const path = pathOnly(endpoint || response?.url);
  if (response) {
    const { status } = response;
    if (status >= 500 || status === 401 || status === 429) return;
    reportError(new Error(`${feature} failed: HTTP ${status}`), {
      level: 'warning',
      tags: {
        feature,
        error_category: 'unexpected_status',
        api_status: String(status),
        request_id: response.headers?.get?.('x-request-id') || undefined,
      },
      contexts: { loader: { feature, endpoint: path, status } },
    });
    return;
  }
  reportError(error, {
    tags: { feature, error_category: error?.name === 'TimeoutError' ? 'timeout' : 'exception' },
    contexts: { loader: { feature, endpoint: path } },
  });
}

const SENSITIVE_KEY =
  /^(authorization|cookie|set-cookie|password|passwd|secret|client_secret|token|access_token|refresh_token|id_token|jwt|api_key|apikey|x-api-key)$/i;
const SENSITIVE_VALUE_PATTERNS = [
  // Reset-password links and exam downloads (?token=<JWT>) carry credentials in the query string.
  [/(^|[?&#;])(token|access_token|refresh_token|id_token|code|key|api_key|password|secret|signature)=[^&#\s]*/gi, '$1$2=[Filtered]'],
  [/\beyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,}/g, '[Filtered JWT]'],
  [/\b(Bearer)\s+[\w.~+/-]+=*/gi, '$1 [Filtered]'],
  [/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, '[Filtered email]'],
];

export function redactUrl(value) {
  if (typeof value !== 'string') return value;
  return SENSITIVE_VALUE_PATTERNS.reduce((s, [re, replacement]) => s.replace(re, replacement), value);
}

function isPlainObject(value) {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** Returns a redacted copy; never mutates objects the app may still be using. */
function redactDeep(value, depth = 0) {
  if (typeof value === 'string') return redactUrl(value);
  if (!value || typeof value !== 'object' || depth > 12) return value;
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1));
  if (value instanceof Error) return redactUrl(`${value.name}: ${value.message}`);
  if (!isPlainObject(value)) return value;
  const out = {};
  for (const [key, v] of Object.entries(value)) {
    if (key === 'sdkProcessingMetadata') out[key] = v;
    else out[key] = SENSITIVE_KEY.test(key) ? '[Filtered]' : redactDeep(v, depth + 1);
  }
  return out;
}

function scrubEvent(event) {
  return redactDeep(event);
}

function readStoredUser() {
  try {
    const raw = localStorage.getItem('user') || localStorage.getItem('adminUser');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readDashboardSection() {
  try {
    return sessionStorage.getItem(USER_DASHBOARD_SECTION_KEY) || undefined;
  } catch {
    return undefined;
  }
}

function clientDetails() {
  const nav = window.navigator || {};
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  return {
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: window.screen ? `${window.screen.width}x${window.screen.height}` : undefined,
    pixel_ratio: window.devicePixelRatio,
    touch: 'ontouchstart' in window || nav.maxTouchPoints > 0,
    installed_pwa:
      window.matchMedia?.('(display-mode: standalone)').matches || nav.standalone === true,
    language: nav.language,
    online: nav.onLine,
    connection_type: connection?.effectiveType,
    connection_rtt_ms: connection?.rtt,
    connection_downlink_mbps: connection?.downlink,
  };
}

function enrichEvent(event) {
  const page = window.location.pathname;
  const section = page.startsWith('/user-dashboard') ? readDashboardSection() : undefined;
  const details = clientDetails();

  event.tags = {
    ...event.tags,
    page,
    ...(section ? { dashboard_section: section } : {}),
    viewport: details.viewport,
    online: String(details.online),
  };
  event.contexts = { ...event.contexts, client: details };

  // Only the id and role identify a student in Sentry; never email, name or IP.
  const user = readStoredUser();
  if (user && (user.id || user.user_id)) {
    event.user = { id: String(user.id || user.user_id), role: user.role };
    event.tags.user_role = user.role;
  } else {
    delete event.user;
  }
  return event;
}

function resolveRequest(input, init) {
  const rawUrl = typeof input === 'string' ? input : input?.url || String(input);
  const method = (init?.method || input?.method || 'GET').toUpperCase();
  let url;
  try {
    url = new URL(rawUrl, window.location.href);
  } catch {
    return null;
  }
  return { url, method };
}

function apiOrigins() {
  const origins = new Set([window.location.origin]);
  for (const value of [process.env.REACT_APP_API_URL, process.env.REACT_APP_ROOT_API_URL]) {
    try {
      if (value) origins.add(new URL(value).origin);
    } catch {
      /* ignore malformed env values */
    }
  }
  return origins;
}

function isApiRequest(url, origins) {
  if (!origins.has(url.origin)) return false;
  const p = url.pathname;
  return !(p.startsWith('/static/') || p.includes('hot-update') || p.startsWith('/sockjs-node'));
}

function normalizePath(p) {
  return p
    .replace(/\/\d+(?=\/|$)/g, '/:id')
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi, '/:id')
    .replace(/\/[0-9a-f]{16,}(?=\/|$)/gi, '/:id');
}

function isUpload(init) {
  const body = init?.body;
  return (
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob)
  );
}

function installApiMonitoring() {
  if (window.__sylleraApiMonitoring || typeof window.fetch !== 'function') return;
  window.__sylleraApiMonitoring = true;

  const origins = apiOrigins();
  const originalFetch = window.fetch.bind(window);

  // Safari rejects in-flight requests with "TypeError: Load failed" while the page unloads,
  // before pagehide fires. Network failures are reported after a short delay: if the page is
  // going away the timer never runs, and a bfcache restore is detected by the generation change.
  let pageGeneration = 0;
  const nextGeneration = () => { pageGeneration += 1; };
  window.addEventListener('pagehide', nextGeneration);
  window.addEventListener('beforeunload', nextGeneration);

  window.fetch = async function sylleraMonitoredFetch(input, init) {
    const req = resolveRequest(input, init);
    if (!req || !isApiRequest(req.url, origins)) return originalFetch(input, init);

    const started = performance.now();
    const route = normalizePath(req.url.pathname);
    const api = { method: req.method, endpoint: req.url.pathname, route };

    try {
      const response = await originalFetch(input, init);
      const durationMs = Math.round(performance.now() - started);
      const details = {
        ...api,
        status: response.status,
        duration_ms: durationMs,
        request_id: response.headers.get('x-request-id') || undefined,
      };
      const tags = { api_route: route, api_status: String(response.status), request_id: details.request_id };

      if (response.status >= 500) {
        Sentry.captureMessage(`API ${response.status}: ${req.method} ${route}`, {
          level: 'error',
          tags,
          contexts: { api: details },
          fingerprint: ['api-error', req.method, route, String(response.status)],
        });
      } else if (durationMs >= SLOW_API_MS && !isUpload(init)) {
        Sentry.captureMessage(`Slow API response (${(durationMs / 1000).toFixed(1)}s): ${req.method} ${route}`, {
          level: 'warning',
          tags,
          contexts: { api: details },
          fingerprint: ['api-slow', req.method, route],
        });
      }
      return response;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        const durationMs = Math.round(performance.now() - started);
        // iOS drops connections of backgrounded tabs/apps; offline or hidden pages are not app bugs.
        const visibility = document.visibilityState;
        const expectedEnvironment = navigator.onLine === false || visibility === 'hidden';
        const generation = pageGeneration;
        markHandled(error);
        setTimeout(() => {
          if (generation !== pageGeneration) return;
          Sentry.captureException(error, {
            level: expectedEnvironment ? 'warning' : 'error',
            tags: { api_route: route, api_status: 'network_error', page_visibility: visibility },
            contexts: { api: { ...api, status: 'network_error', duration_ms: durationMs } },
            fingerprint: ['api-network-error', req.method, route],
          });
        }, 1000);
      }
      throw error;
    }
  };
}

function parseSampleRate(value, fallback) {
  const n = Number(value);
  return value !== undefined && value !== '' && n >= 0 && n <= 1 ? n : fallback;
}

if (RAW_DSN && !DSN) {
  // eslint-disable-next-line no-console
  console.warn('[monitoring] REACT_APP_SENTRY_DSN is not a valid Sentry DSN; monitoring disabled.');
}

// Monitoring must never prevent the app from booting (this module is the first import).
try {
  if (isMonitoringEnabled) initSentry();
} catch (error) {
  isMonitoringEnabled = false;
  // eslint-disable-next-line no-console
  console.warn('[monitoring] Sentry initialization failed; monitoring disabled.', error);
}

function initSentry() {
  Sentry.init({
    dsn: DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: parseSampleRate(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
      IS_PROD ? 0.1 : 1.0
    ),
    sendDefaultPii: false,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
    beforeSend: (event) => scrubEvent(enrichEvent(event)),
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb(breadcrumb, hint) {
      if (
        (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
        hint?.startTimestamp &&
        hint?.endTimestamp
      ) {
        breadcrumb.data = {
          ...breadcrumb.data,
          duration_ms: Math.round(hint.endTimestamp - hint.startTimestamp),
        };
      }
      // Full redaction happens in beforeSend; scrub Sentry-owned fields early so stored breadcrumbs stay clean.
      if (breadcrumb.message) breadcrumb.message = redactUrl(breadcrumb.message);
      for (const key of ['url', 'from', 'to']) {
        if (typeof breadcrumb.data?.[key] === 'string') breadcrumb.data[key] = redactUrl(breadcrumb.data[key]);
      }
      return breadcrumb;
    },
  });
  installApiMonitoring();
}

export const reactRootErrorHandlers = isMonitoringEnabled
  ? {
      onUncaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler(),
    }
  : {};
